import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Text, Group, Stack, Alert, Loader, FileButton } from '@mantine/core';
import { FaFilePdf, FaFileWord, FaFileExcel, FaFileAlt, FaFileImage, FaDownload, FaTrash, FaUpload } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import { BASE_URL } from '../Redux/apiConfig';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/plain',
  'image/jpeg',
  'image/png',
  'image/jpg'
];

const DocumentUploadModal = ({ opened, onClose, selectedData }) => {
  const intl = useIntl();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (opened && selectedData?.productID) {
      fetchDocuments();
    }
  }, [opened, selectedData]);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${BASE_URL}/getProductDocuments/${selectedData.productID}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        setDocuments(data.documents || []);
      } else {
        setError(data.error || intl.formatMessage({ id: 'documentModal.errorLoadDocuments' }));
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(intl.formatMessage({ id: 'documentModal.errorLoadDocuments' }));
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType.includes('pdf')) return <FaFilePdf color="#dc3545" size={20} />;
    if (contentType.includes('word')) return <FaFileWord color="#2b579a" size={20} />;
    if (contentType.includes('excel') || contentType.includes('sheet')) return <FaFileExcel color="#217346" size={20} />;
    if (contentType.includes('image')) return <FaFileImage color="#6c757d" size={20} />;
    return <FaFileAlt color="#6c757d" size={20} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(intl.formatMessage({ id: 'documentModal.errorFileType' }, { fileName: file.name }));
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError(intl.formatMessage({ id: 'documentModal.errorFileSize' }, { fileName: file.name }));
        continue;
      }

      try {
        await uploadDocument(file);
      } catch (err) {
        console.error('Error uploading file:', err);
        setError(intl.formatMessage({ id: 'documentModal.errorUploadFailed' }, { fileName: file.name }));
      }
    }

    setUploading(false);
  };

  const uploadDocument = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result.split(',')[1];
          const token = localStorage.getItem('authToken');

          const response = await fetch(
            `${BASE_URL}/uploadProductDocument/${selectedData.productID}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                fileName: file.name,
                fileData: base64Data,
                fileType: file.type
              })
            }
          );

          const data = await response.json();

          if (data.success) {
            setSuccess(intl.formatMessage({ id: 'documentModal.successUploaded' }, { fileName: file.name }));
            await fetchDocuments();
            resolve(data);
          } else {
            reject(new Error(data.error));
          }
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = () => reject(new Error(intl.formatMessage({ id: 'documentModal.errorReadFile' })));
      reader.readAsDataURL(file);
    });
  };

  const handleDownload = async (fileName) => {
    setError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${BASE_URL}/downloadProductDocument/${selectedData.productID}/${encodeURIComponent(fileName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        window.open(data.downloadUrl, '_blank');
      } else {
        setError(data.error || intl.formatMessage({ id: 'documentModal.errorDownloadFailed' }));
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      setError(intl.formatMessage({ id: 'documentModal.errorDownloadFailed' }));
    }
  };

  const handleDelete = async (fileName) => {
    if (!window.confirm(intl.formatMessage({ id: 'documentModal.confirmDelete' }, { fileName }))) return;

    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${BASE_URL}/deleteProductDocument/${selectedData.productID}/${encodeURIComponent(fileName)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(intl.formatMessage({ id: 'documentModal.successDeleted' }, { fileName }));
        await fetchDocuments();
      } else {
        setError(data.error || intl.formatMessage({ id: 'documentModal.errorDeleteFailed' }));
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(intl.formatMessage({ id: 'documentModal.errorDeleteFailed' }));
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${intl.formatMessage({ id: 'documentModal.title' })} - ${selectedData?.productName || intl.formatMessage({ id: 'documentModal.product' })}`}
      size="xl"
      centered
    >
      <Stack spacing="md">
        <FileButton
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
          multiple
          disabled={uploading || loading}
        >
          {(props) => (
            <Button {...props} leftIcon={<FaUpload />} disabled={uploading || loading}>
              {intl.formatMessage({ id: 'documentModal.uploadButton' })}
            </Button>
          )}
        </FileButton>

        <Text size="xs" color="dimmed">
          {intl.formatMessage({ id: 'documentModal.supportedFormats' })}
        </Text>

        {error && (
          <Alert title="Error" color="red" withCloseButton onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert title="Success" color="green" withCloseButton onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {uploading && (
          <Group position="center">
            <Loader size="sm" />
            <Text>{intl.formatMessage({ id: 'documentModal.uploading' })}</Text>
          </Group>
        )}

        {loading ? (
          <Group position="center" style={{ padding: '2rem' }}>
            <Loader size="md" />
            <Text>{intl.formatMessage({ id: 'documentModal.loadingDocuments' })}</Text>
          </Group>
        ) : documents.length === 0 ? (
          <Text align="center" color="dimmed" style={{ padding: '2rem' }}>
            {intl.formatMessage({ id: 'documentModal.noDocuments' })}
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th style={{ width: '50px' }}>{intl.formatMessage({ id: 'documentModal.tableType' })}</th>
                <th>{intl.formatMessage({ id: 'documentModal.tableFileName' })}</th>
                <th style={{ width: '100px' }}>{intl.formatMessage({ id: 'documentModal.tableSize' })}</th>
                <th style={{ width: '180px' }}>{intl.formatMessage({ id: 'documentModal.tableUploaded' })}</th>
                <th style={{ width: '120px' }}>{intl.formatMessage({ id: 'documentModal.tableActions' })}</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc, index) => (
                <tr key={index}>
                  <td style={{ textAlign: 'center' }}>{getFileIcon(doc.contentType)}</td>
                  <td style={{ wordBreak: 'break-word' }}>{doc.name}</td>
                  <td>{formatFileSize(doc.size)}</td>
                  <td>{formatDate(doc.lastModified)}</td>
                  <td>
                    <Group spacing="xs">
                      <Button
                        size="xs"
                        color="blue"
                        onClick={() => handleDownload(doc.name)}
                        title={intl.formatMessage({ id: 'documentModal.download' })}
                      >
                        <FaDownload />
                      </Button>
                      <Button
                        size="xs"
                        color="red"
                        onClick={() => handleDelete(doc.name)}
                        title={intl.formatMessage({ id: 'documentModal.delete' })}
                      >
                        <FaTrash />
                      </Button>
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Stack>
    </Modal>
  );
};

export default DocumentUploadModal;